import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {AbstractControl, UntypedFormArray, FormControl, UntypedFormGroup} from '@angular/forms';
import {ToastService} from '../../../../main/services';
import {TenderAnalyticsSettingsCurrencyRatesService, TenderAnalyticsSettingsService} from '../../../services';
import {User} from '../../../../auth/models';
import {ConversionFactor, TenderAnalyticsSettings, TenderAnalyticsSettingsCurrencyRates} from '../../../models';
import {FormValidationErrorsHelper} from '../../../../common/helpers/forms/form-validation-errors-helper';
import {tenderAnalyticsConversionFactorForm} from '../../../forms/tender-analytics-conversion-factor.form';
import {DateTimeHelper} from '../../../../common/helpers/datetime/datetime.helper';
import moment, { Moment } from 'moment';
import {tenderAnalyticsCurrencyRatesForm} from "../../../forms";
import {DECIMAL_MASK} from "../../../../common/helpers";
import { finalize } from 'rxjs/operators';
import { UnitRatesService } from '../../../../common/services';


@Component({
    selector: 'jft-tender-conversion-factors',
    templateUrl: './conversion-factors.component.html',
    styleUrls: ['conversion-factors.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class ConversionFactorsComponent implements OnInit {

    public form: UntypedFormGroup;
    public conversionFactor: ConversionFactor;
    public analyticSettingCurrencyRates: TenderAnalyticsSettingsCurrencyRates[];
    public analyticCurrencyRatesForm: UntypedFormArray;
    public loading: boolean = false;
    public tenderId: string;
    public decimalMask = DECIMAL_MASK;
    /** The standard Jet A-1 factor (USG per MT), from the backend; never typed here. */
    public standardUsgPerMt: number = null;

    constructor(
        protected route: ActivatedRoute,
        private service: TenderAnalyticsSettingsService,
        private analyticsCurrencyRatesService: TenderAnalyticsSettingsCurrencyRatesService,
        private toast: ToastService,
        public user: User,
        private unitRatesService: UnitRatesService,
    ) {

    }

    public ngOnInit() {
        let data = this.route.snapshot.data;
        let parentData = this.route.snapshot.parent.data;
        this.tenderId = this.route.snapshot.parent.parent.params.id;
        this.conversionFactor = data['conversion_factor'];
        this.analyticSettingCurrencyRates = data['analytic_currency_rates'];
        this.unitRatesService.fetchConversion().subscribe(c => this.standardUsgPerMt = c.jet_a1.default);

        this.form = tenderAnalyticsConversionFactorForm(this.conversionFactor);
        this.updateRateForm();

        // Tender Settings are read-only for non-Manager roles.
        if (!this.user.canManageAdvanced()) {
            this.form.disable();
            this.analyticCurrencyRatesForm.disable();
        }
    }

    private updateRateForm() {
        this.analyticCurrencyRatesForm = tenderAnalyticsCurrencyRatesForm(this.analyticSettingCurrencyRates)
    }

    public onCurrency(currencyId: number) {
        this.form.controls.currency_id.setValue(currencyId);
    }

    public onUnit(unitId: number) {
        this.form.controls.units_id.setValue(unitId);
    }

    public save() {
        if (this.form.invalid === true) {
            this.toast.error('Invalid data');
            return;
        }
        this.form.disable();
        this.loading = true;

        this.service.saveConversionFactor(this.form.value, this.tenderId)
            .pipe(finalize(() => this.form.enable()))
            .subscribe(
                (data: ConversionFactor) => {
                this.conversionFactor = data;
                this.toast.success(`Conversion Factor has been updated`);
                this.saveAnalyticsCurrencyRates();
                this.loading = false;
            },
            err => {
                this.form.enable();
                this.toast.error(err);
                this.loading = false;
            });
    }

    public getAnalyticsCurrencyRates() {
        this.analyticsCurrencyRatesService.fetch(this.tenderId).subscribe((items: TenderAnalyticsSettingsCurrencyRates[]) => {
            this.analyticSettingCurrencyRates = items;
        })
    }

    public saveAnalyticsCurrencyRates() {
        this.analyticsCurrencyRatesService.save(this.analyticCurrencyRatesForm.value, this.tenderId)
            .subscribe((items: TenderAnalyticsSettingsCurrencyRates[]) => {
            this.analyticSettingCurrencyRates = items;
            this.updateRateForm();
        }, err => {
            this.toast.error(err);
        });
    }

    public getFirstFormGroupErrorLabel(form: UntypedFormGroup) {
        let formValidationErrors = new FormValidationErrorsHelper();
        return formValidationErrors.getFirstErrorFormLabel(form);
    }

    public showErrorBlock(control: AbstractControl): boolean {
        return control.errors && (control.dirty || control.touched);
    }

    public getFormGroupErrorClasses(control: AbstractControl): {} {
        return {
            'is-invalid': control.invalid && control.touched
        };
    }

    get maxDate(): {} {
        return DateTimeHelper.changeToYMDFormat(moment());
    }
}
